import type {
  ConfigError,
  ConfigMessage,
  ConfigMessageInfo,
  EntityDetailError,
} from '@web-config-app/core';
import React, { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { nanoid } from 'nanoid';

/**
 * Namespace keys used to slice `additionalValidationErrors`. Each feature
 * that writes per-field validation errors to the user-feedback store
 * picks a stable key here so other features' slices don't collide.
 *
 * - `ApiValidation` — backend validation errors mirrored from
 *   `useEntityOperationError`. The entity-details page reads this slice
 *   to inject errors into JSON Forms' validation state via the
 *   `additionalErrors` prop so individual controls can render them
 *   inline.
 *
 * Lives next to the store (not in the consuming hook) so the reset
 * logic and slice clearing helpers can reference the keys directly.
 */
export const AdditionalValidationErrorKey = {
  ApiValidation: 'api-validation',
} as const;

/**
 * Store used to track user feedback messages in the config app including error messages and validation
 */
export interface ConfigUserFeedbackStoreState {
  /**
   * Populated on entity operation to show a loading message and state on the entity details page
   */
  entityOperationLoadingMsg: string | null;
  setEntityOperationLoadingMsg: (value: string | null) => void;
  /**
   * Populated on config errors we want to track, egL onError of ReactQuery requests of Entity Operations ('get', 'update', 'create', 'duplicate')
   */
  configError: ConfigError | null;
  /**
   * Provides the ability to set configErrors, used onError in ReactQuery
   */
  setConfigError: (error: ConfigError | null) => void;
  /**
   * * Storage for validation errors that occur outside the main instance of the Entity Form, stored with a unique key of where the error occurred. These errors are showed in the Error banner when it is visible.
   */
  additionalValidationErrors: {
    [key: string]: EntityDetailError[];
  };
  setAdditionalValidationErrors: (
    key: string,
    errors: EntityDetailError[],
  ) => void;
  removeAdditionalValidationErrors: (key: string) => void;
  /**
   * Provides the ability to reset the configErrors back to null
   */
  resetConfigError: () => void;
  /**
   * When true, form field input validation states and messages will be shown to users and validation will be re-called in the until all form errors are resolved
   */
  showFormValidation: boolean;
  setShowFormValidation: (value: boolean) => void;
  /**
   * When true, error banner (If there are configErrors) will be shown to users
   */
  showErrorBanner: boolean;
  setShowErrorBanner: (value: boolean) => void;
  /**
   * * an array of error keywords that should be filtered out of the errors ajv g
   */
  filteredErrorKeywords: string[];
  setFilteredErrorKeywords: (value: string[]) => void;

  /**
   * Optional override for validation keywords. When set (including an empty array), it takes precedence over tenant configuration.
   * Use null to clear the override and return to tenant-derived keywords.
   */
  errorFilterKeywordsActionOverride: string[] | null;
  setErrorFilterKeywordsActionOverride: (value: string[] | null) => void;

  addConfigMessage(message: ConfigMessageInfo): void;
  removeConfigMessage(id: string): void;
  configMessages: ConfigMessage[];
  resetConfigMessages(): void;
  /**
   * * Reset the store properties to their clean initial state
   */
  resetConfigUserFeedbackStore: () => void;
}

/**
 * Create the Zustand store for managing user feedback messaging
 */
export const configUserFeedbackStore =
  createStore<ConfigUserFeedbackStoreState>()((set, get) => ({
    entityOperationLoadingMsg: null,
    setEntityOperationLoadingMsg: (value) => {
      set({
        entityOperationLoadingMsg: value,
      });
    },
    showFormValidation: false,
    setShowFormValidation: (value) => {
      set({
        showFormValidation: value,
      });
    },
    showErrorBanner: false,
    setShowErrorBanner: (value) => {
      set({
        showErrorBanner: value,
      });
    },
    filteredErrorKeywords: [],
    setFilteredErrorKeywords: (value) => {
      set({
        filteredErrorKeywords: value,
      });
    },
    errorFilterKeywordsActionOverride: null,
    setErrorFilterKeywordsActionOverride: (value) =>
      set({ errorFilterKeywordsActionOverride: value }),
    configError: null,
    setConfigError: (error: ConfigError | null) => {
      set({
        configError: error,
      });
    },
    additionalValidationErrors: {},
    setAdditionalValidationErrors: (key, errors: EntityDetailError[]) => {
      set((state) => ({
        additionalValidationErrors: {
          ...state.additionalValidationErrors,
          [key]: errors,
        },
      }));
    },
    removeAdditionalValidationErrors: (key: string) => {
      set((state) => {
        const { [key]: removed, ...rest } = state.additionalValidationErrors;
        return { additionalValidationErrors: rest };
      });
    },
    resetConfigError: () => set({ configError: null }),
    configMessages: [],
    addConfigMessage: (message: ConfigMessage) => {
      // assign a unique id to the message using nanoid
      const newMessages = [
        ...get().configMessages,
        { ...message, id: nanoid() },
      ];
      set({
        configMessages: newMessages,
      });
    },
    removeConfigMessage: (id: string) => {
      const newMessages = get().configMessages.filter(
        (message) => message.id !== id,
      );
      set({
        configMessages: newMessages,
      });
    },
    resetConfigMessages: () => set({ configMessages: [] }),
    resetConfigUserFeedbackStore: () =>
      set((state) => {
        // Clear only the api-validation slice; leave other slices
        // (e.g. `localized-fields-validation` or rule-template keys) intact
        // so this reset doesn't stomp on validation owned by other features.
        // Save-success handlers and the entity-details page's unmount effect
        // both call this, so api-validation errors disappear on a successful
        // save and don't leak between entities.
        const {
          [AdditionalValidationErrorKey.ApiValidation]: removed,
          ...remainingAdditionalValidationErrors
        } = state.additionalValidationErrors;
        return {
          configError: null,
          configMessages: [],
          filteredErrorKeywords: [],
          showFormValidation: false,
          showErrorBanner: false,
          additionalValidationErrors: remainingAdditionalValidationErrors,
        };
      }),
  }));

/**
 * React Context for providing the user feedback store
 */
export const ConfigUserFeedbackContext = createContext<
  typeof configUserFeedbackStore | null
>(null);

/**
 * Provider to make the ConfigUserFeedbackStore available to its children, this must be used around an app in order for the app to have access to the useConfigUserFeedback hooks (and therefore the config error management)
 */
export function ConfigUserFeedbackProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  /**
   * create a stable reference to the store using useRef, ensuring the store is only created once
   */
  const store = useRef(configUserFeedbackStore);

  return (
    <ConfigUserFeedbackContext.Provider value={store.current}>
      {children}
    </ConfigUserFeedbackContext.Provider>
  );
}

/**
 * useConfigUserFeedback provides access to the `configUserFeedbackStore`'s state and actions.
 *
 * Utilizes Zustand's `useStore` with selectors for optimized rendering.
 * Components will only re-render when the selected or derived values from the store change.
 *
 * @template T - the type of the value returned by the selector function.
 * @param selector - a function that selects or derives the desired data from the `ConfigErrorsStoreState`.
 * @returns the selected or derived value from the store.
 *
 * @example
 * // Access a specific property
 * const configError = useConfigUserFeedback(state => state.configError);
 *
* // Access multiple properties
 *   const { setConfigError, resetConfigError } = useConfigUserFeedback((state) => ({
    setConfigError: state.setConfigError,
    resetConfigError: state.resetConfigError,
  }));
 * 
 * // Derive a new value
 * const hasError = useConfigUserFeedback(state => state.configError !== null);
 */
export function useConfigUserFeedback<T>(
  selector: (state: ConfigUserFeedbackStoreState) => T,
): T {
  const configUserFeedbackStoreContext = useContext(ConfigUserFeedbackContext);
  if (!configUserFeedbackStoreContext) {
    throw new Error(
      'useConfigUserFeedback must be used within the ConfigUserFeedbackProvider',
    );
  }
  /**
   * use Zustand's useStore to get the store's state and actions
   */
  return useStore(configUserFeedbackStoreContext, selector);
}
